import * as React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import type { Category, User } from "@/types/domain.types"
import { logger } from "@/lib/logger"

const categoryFormSchema = z.object({
  name: z.string().min(1, "Category name is required").max(100, "Name too long"),
  description: z.string().max(500, "Description too long").optional(),
  emoji: z.string().optional(),
  default_splits: z.array(z.object({
    user_id: z.string(),
    weight: z.number().min(0.1, "Weight must be at least 0.1"),
  })).optional(),
})

type CategoryFormData = z.infer<typeof categoryFormSchema>

interface CategoryFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  category?: Category
  groupMembers: User[]
  onSubmit: (data: CategoryFormData) => Promise<void>
  isSubmitting?: boolean
}

const CATEGORY_EMOJIS = [
  "🍕", "🛒", "⛽", "🏠", "💡", "🚗", "🎬", "🎵", "📱", "💊",
  "🏥", "🎓", "📚", "✈️", "🏖️", "🎉", "🍷", "☕", "🍔", "🎮",
  "👕", "💄", "🛍️", "🎯", "🏃‍♂️", "🎸", "📷", "🎨", "🔧", "💰"
]

// Helper function to find greatest common divisor
const gcd = (a: number, b: number): number => {
  if (b === 0) return a
  return gcd(b, a % b)
}

// Helper function to find GCD of an array of numbers
const gcdArray = (numbers: number[]): number => {
  if (numbers.length === 0) return 1
  return numbers.reduce((acc, num) => gcd(Math.abs(acc), Math.abs(num)))
}

// Helper function to simplify weights
const simplifyWeights = (splits: Array<{ user_id: string; weight: number }>) => {
  const weights = splits.map(s => s.weight)
  const commonDivisor = gcdArray(weights)

  if (commonDivisor > 1) {
    return splits.map(split => ({
      ...split,
      weight: split.weight / commonDivisor
    }))
  }

  return splits
}

export const CategoryForm: React.FC<CategoryFormProps> = ({
  open,
  onOpenChange,
  category,
  groupMembers,
  onSubmit,
  isSubmitting = false,
}) => {
  const [selectedEmoji, setSelectedEmoji] = React.useState(category?.emoji || "")
  const [enableSplits, setEnableSplits] = React.useState(
    !!(category?.default_splits && category.default_splits.length > 0)
  )

  // Initialize default splits
  const defaultSplits = React.useMemo(() => {
    return category?.default_splits || groupMembers.map(member => ({
      user_id: member.id,
      weight: 1,
    }))
  }, [category?.default_splits, groupMembers])

  const form = useForm<CategoryFormData>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      name: "",
      description: "",
      emoji: "",
      default_splits: [],
    },
  })

  // Reset form when category changes or dialog opens
  React.useEffect(() => {
    if (open) {
      form.reset({
        name: category?.name || "",
        description: category?.description || "",
        emoji: category?.emoji || "",
        default_splits: defaultSplits,
      })
      setSelectedEmoji(category?.emoji || "")
      setEnableSplits(!!(category?.default_splits && category.default_splits.length > 0))
    }
  }, [open, category, form, defaultSplits])

  // Initialize with the same default splits
  const [watchedSplits, setWatchedSplits] = React.useState(defaultSplits)

  React.useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "default_splits" || name?.startsWith("default_splits")) {
        const splits = (value.default_splits || defaultSplits).map(split => ({
          user_id: split?.user_id || '',
          weight: split?.weight || 1
        }))
        setWatchedSplits(splits)
      }
    })
    return () => subscription.unsubscribe()
  }, [form, defaultSplits])

  React.useEffect(() => {
    form.setValue("emoji", selectedEmoji)
  }, [selectedEmoji, form])

  React.useEffect(() => {
    if (!enableSplits) {
      form.setValue("default_splits", [])
      setWatchedSplits([])
    } else if (watchedSplits.length === 0) {
      form.setValue("default_splits", defaultSplits)
      setWatchedSplits(defaultSplits)
    }
  }, [enableSplits, form, defaultSplits, watchedSplits.length])

  const handleSubmit = async (data: CategoryFormData) => {
    try {
      const submitData = {
        ...data,
        default_splits: enableSplits ? data.default_splits : [],
      }
      await onSubmit(submitData)
      onOpenChange(false)
      if (!category) {
        form.reset()
        setSelectedEmoji("")
        setEnableSplits(false)
      }
    } catch (error) {
      logger.error("Failed to submit category:", error)
    }
  }

  const totalWeight = watchedSplits.reduce((sum, split) => {
    const weight = typeof split.weight === 'number' ? split.weight : parseFloat(split.weight) || 0
    return sum + weight
  }, 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {category ? "Edit Category" : "Create New Category"}
          </DialogTitle>
          <DialogDescription>
            {category
              ? "Update your category details below."
              : "Create a new category to organize your expenses."
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Food, Transportation"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Brief description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-3">
              <FormLabel>Category Icon (Optional)</FormLabel>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={selectedEmoji === "" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedEmoji("")}
                >
                  None
                </Button>
                {CATEGORY_EMOJIS.map((emoji) => (
                  <Button
                    key={emoji}
                    type="button"
                    variant={selectedEmoji === emoji ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedEmoji(emoji)}
                  >
                    {emoji}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <FormLabel>Default Split Settings</FormLabel>
                  <FormDescription>
                    Set custom split ratios for this category (optional)
                  </FormDescription>
                </div>
                <Button
                  type="button"
                  variant={enableSplits ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEnableSplits(!enableSplits)}
                >
                  {enableSplits ? "Enabled" : "Disabled"}
                </Button>
              </div>

              {enableSplits && (
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground">
                    When creating expenses in this category, these weights will be used as defaults.
                    Higher weights mean larger portions of the expense.
                  </div>

                  <div className="space-y-4">
                    {groupMembers.map((member, index) => {
                      const split = watchedSplits[index]
                      const rawWeight = split?.weight || 1
                      const splitWeight = typeof rawWeight === 'number' ? rawWeight : parseFloat(rawWeight) || 1
                      const percentage = totalWeight > 0 ? (splitWeight / totalWeight * 100) : 0
                      return (
                        <div key={member.id} className="space-y-2">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={member.avatar_url} />
                              <AvatarFallback className="text-xs">
                                {member.name.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium w-32">{member.name}</span>
                            <div className="flex-1 bg-muted rounded-full h-8 relative overflow-hidden">
                              <div
                                className="absolute h-full bg-primary/80 flex items-center justify-end pr-2 transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              >
                                {percentage > 15 && (
                                  <span className="text-xs font-medium text-white">
                                    {percentage.toFixed(0)}%
                                  </span>
                                )}
                              </div>
                              {percentage <= 15 && (
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground">
                                  {percentage.toFixed(0)}%
                                </span>
                              )}
                            </div>
                            <FormField
                              control={form.control}
                              name={`default_splits.${index}.weight`}
                              render={({ field }) => (
                                <FormItem className="w-20">
                                  <FormControl>
                                    <Input
                                      type="number"
                                      step="1"
                                      min="1"
                                      placeholder="1"
                                      className="text-center"
                                      value={field.value?.toString() || ''}
                                      onChange={(e) => {
                                        const numericValue = parseFloat(e.target.value) || 1

                                        // Update React Hook Form field value as number
                                        field.onChange(numericValue)

                                        const newSplits = [...watchedSplits]
                                        newSplits[index] = {
                                          user_id: member.id,
                                          weight: numericValue
                                        }
                                        form.setValue('default_splits', newSplits)
                                        setWatchedSplits(newSplits)

                                        const simplified = simplifyWeights(newSplits)
                                        if (JSON.stringify(simplified) !== JSON.stringify(newSplits)) {
                                          form.setValue('default_splits', simplified)
                                          setWatchedSplits(simplified)
                                        }
                                      }}
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {totalWeight > 0 && (
                    <div className="text-sm text-center">
                      <Badge variant="outline">
                        Total weight: {totalWeight.toFixed(1)}
                      </Badge>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? "Saving..."
                  : category
                    ? "Update Category"
                    : "Create Category"
                }
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}